import { Link } from "react-router-dom";
import { ArrowRight, Check, Lock } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { track } from "../lib/analytics";

const FEATURES = [
  "Private wellness profile",
  "Personal assessment history",
  "Interactive visual summaries",
  "Wellness timeline",
  "Personalized educational insights",
];

const DEMO_ROWS = [
  { name: "Physical Wellness", first: 62, latest: 76 },
  { name: "Environmental Wellness", first: 58, latest: 70 },
  { name: "Emotional Wellness", first: 44, latest: 58 },
  { name: "Spiritual Wellness", first: 70, latest: 84 },
];

export const JourneyPreview = () => (
  <section
    id="journey-preview"
    data-testid="journey-preview-section"
    className="relative bg-sage/50 py-24 sm:py-32"
  >
    <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
      <Reveal className="order-2 lg:order-1 lg:col-span-6">
        <div className="relative mx-auto max-w-lg rounded-[2.5rem] border border-forest/10 bg-white/75 p-7 shadow-[0_25px_60px_rgba(23,63,53,0.12)] sm:p-9">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">
              Wellness Timeline
            </p>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-sage px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-forest">
              <Lock className="h-3 w-3" />
              Demonstration data
            </span>
          </div>
          <div className="mt-7 flex flex-col gap-6">
            {DEMO_ROWS.map((row) => (
              <div key={row.name}>
                <div className="flex items-baseline justify-between">
                  <p className="text-sm font-semibold text-forest">{row.name}</p>
                  <p className="text-xs text-taupe">
                    {row.first} → {row.latest}
                  </p>
                </div>
                <div className="mt-2 flex flex-col gap-1.5">
                  <div className="h-2 rounded-full bg-forest/10">
                    <div
                      className="h-2 rounded-full bg-forest/35"
                      style={{ width: `${row.first}%` }}
                    />
                  </div>
                  <div className="h-2 rounded-full bg-forest/10">
                    <div className="h-2 rounded-full bg-gold" style={{ width: `${row.latest}%` }} />
                  </div>
                </div>
              </div>
            ))}
            <div className="rounded-2xl bg-sage/70 px-5 py-4">
              <p className="text-sm font-semibold text-forest">Inheritance & Personal History</p>
              <p className="mt-1 text-xs leading-relaxed text-charcoal/65">
                Explored as personal context that informs your story. Never graded, never scored.
              </p>
            </div>
          </div>
          <div className="mt-6 flex items-center gap-6 border-t border-forest/10 pt-5 text-xs text-charcoal/65">
            <span className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-forest/35" aria-hidden="true" />
              First discovery · Jan 2026
            </span>
            <span className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-gold" aria-hidden="true" />
              Latest discovery · Jun 2026
            </span>
          </div>
        </div>
      </Reveal>
      <div className="order-1 lg:order-2 lg:col-span-6 lg:pl-8">
        <Reveal>
          <Overline>Track Your Journey</Overline>
          <h2 className="font-serif text-3xl leading-tight text-forest sm:text-4xl lg:text-5xl">
            Wellness Is a Journey. <em className="italic text-gold">See How Yours Evolves.</em>
          </h2>
          <p className="mt-7 text-lg font-light leading-relaxed text-charcoal/75">
            Your life changes, your priorities shift, and your daily habits evolve. With your
            personal Rooted Wellness account, you can return, repeat your discovery assessment,
            and explore how your self-reported experiences have changed over time.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-3">
                <Check className="mt-1 h-4 w-4 shrink-0 text-gold" />
                <span className="text-sm leading-relaxed text-charcoal/75">{f}</span>
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.18}>
          <Link
            to="/my-wellness-journey"
            data-testid="journey-preview-cta"
            onClick={() => track("discovery_cta_click", { location: "homepage-journey-preview" })}
            className="group mt-9 inline-flex items-center gap-2 rounded-full border border-forest/30 px-8 py-4 text-sm font-semibold text-forest transition-all duration-300 hover:border-gold hover:text-gold-dark"
          >
            Explore My Wellness Journey
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);
